import { IMultiStopLegDatObjects } from 'types/graphqlTypes'
import { usePriceRequestAppliedRulesQuery } from 'apps/marketplace/pages/spot-quote-tool/graphql/PriceRequestAppliedRules'
import { IPriceRequestAppliedRulesFragment } from 'apps/marketplace/pages/spot-quote-tool/graphql/PriceRequestAppliedRulesFragment'
import { IPriceRequestDetailsFragment } from 'apps/marketplace/pages/spot-quote-tool/graphql/PriceRequestDetailsFragment'
import {
  IAdditionalStops,
  IFuelSurchargeCalculation,
  IMetadata,
  IRates,
  ISpotBiddingRules,
} from 'apps/marketplace/store/amQuoting/types'
import { ICostControlConfig } from 'apps/spot-quote-control-panel/types/costControlConfig'
import { ISpotBiddingRule } from 'apps/spot-quote-control-panel/types/spotBiddingConfig'
import { DISPLAY } from 'constants/display'

export const usePriceRequestAppliedRules = (priceRequest: IPriceRequestDetailsFragment) => {
  const { data, loading } = usePriceRequestAppliedRulesQuery({
    variables: { priceRequestId: priceRequest.id },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  })

  const { priceRequestAppliedRules } = data || {}

  const appliedRules = priceRequestAppliedRules as IPriceRequestAppliedRulesFragment
  const { priceRequestAppliedRulesMetadata } = appliedRules || {}
  const {
    adjustedCostBasis,
    appliedCostControlConfig,
    appliedSpotBiddingRules,
    appliedStrikePriceRules,
    dsCostPremiumPct,
    finalManualCostAdjustmentPercent,
    fuelSurchargeCalculation,
    isDoNotBid,
    multiStopBucketUsed,
    strategyChosen,
  } = priceRequestAppliedRulesMetadata || {}
  let { additionalStops } = priceRequestAppliedRulesMetadata || {}
  let { multiStopLegDatObjects } = priceRequestAppliedRulesMetadata || {}

  // this will apply to older quotes that do not have specific info about additional stops stored
  // we still want to not show data in the DAT or TCM cards, and this helps with that
  if ((additionalStops === null || additionalStops === undefined) && priceRequest.additionalStops) {
    additionalStops = []
  }
  if (
    (multiStopLegDatObjects === null || multiStopLegDatObjects === undefined) &&
    priceRequest.additionalStops
  ) {
    multiStopLegDatObjects = []
  }

  // reason is often stored as null on the BE but is required for ISpotBiddingRule
  // the ISpotBiddingRule type is used extensively so i don't want to  mess with it
  const sbr = appliedSpotBiddingRules?.map(rule => {
    return { ...rule, reason: rule.reason ?? DISPLAY.empty }
  })
  const spr = appliedStrikePriceRules?.map(rule => {
    return { ...rule, reason: rule.reason ?? DISPLAY.empty }
  })

  // formatting the GQL response so that it better fits with the existing SpotRulesCard component
  const spotBiddingRules = {
    appliedRules: sbr ? (sbr as ISpotBiddingRule[]) : [],
  } as ISpotBiddingRules
  const strikePriceRules = {
    appliedRules: spr ? (spr as ISpotBiddingRule[]) : [],
  } as ISpotBiddingRules

  const adjustmentPct =
    typeof finalManualCostAdjustmentPercent === 'number'
      ? finalManualCostAdjustmentPercent * 100
      : finalManualCostAdjustmentPercent

  const metadata = {
    spotBiddingRules,
    strikePriceRules,
    isDoNotBid,
    strategyChosen,
    finalManualCostAdjustmentPercent: adjustmentPct,
    dsCostPremiumPct,
    fuelSurchargeCalculation: fuelSurchargeCalculation as IFuelSurchargeCalculation,
    appliedCostControlConfig: appliedCostControlConfig as ICostControlConfig,
    adjustedCostBasis,
    multiStopBucketUsed: Number(multiStopBucketUsed),
    additionalStops: additionalStops as IAdditionalStops[],
    multiStopLegDatObjects: multiStopLegDatObjects as IMultiStopLegDatObjects[],
  } as IMetadata

  const { premiums, pricingResponse } = priceRequest
  const { quoteWithFuel, quoteWithoutFuel } = premiums || {}
  const { datRate, highDatRate, lowDatRate, tcm25, tcm40, tcm50, tcm75 } = pricingResponse || {}
  const rates: IRates = {
    allIn: quoteWithFuel || 0,
    linehaul: quoteWithoutFuel || 0,
    datEstimatedAllIn: datRate || 0,
    datHighAllIn: highDatRate || 0,
    datLowAllIn: lowDatRate || 0,
    transfixCostPrediction40Percent: tcm40 || 0,
    transfixCostPredictionBottom: tcm25 || 0,
    transfixCostPredictionEstimate: tcm50 || 0,
    transfixCostPredictionTop: tcm75 || 0,
  }

  const spotRate = { priceRequestId: Number(priceRequest.id), metadata, rates }
  return { spotRate, loading }
}
